// ═══════════════════════════════════════════════════════════════════════
// Pilotage › Dossiers en attente (contrat docs/rh/workflows/CONTRAT_PROCHAIN_ACTEUR.md § 6).
// Tous les dossiers dont une étape attend une action : type, demandeur, étape, qui doit agir (personne +
// fonction), depuis quand. LECTURE SEULE. Source : GET /api/workflow/en-attente (droit
// voir_dossiers_en_attente ou rôle RH à portée globale ; périmètre et confidentialité appliqués par le serveur).
// Les filtres sont envoyés au serveur ; l'écran ne calcule aucun valideur ni aucune attente. La mise en
// évidence de l'ancienneté (≥ 7 jours, ≥ 30 jours) est un repère visuel seulement, sans effet sur les dossiers.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Hourglass, Eye, ExternalLink, ArrowDownWideNarrow, ArrowUpNarrowWide, CheckCircle2, UserX } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Pagination, formaterDate } from '../../design/composants';
import { Fenetre } from '../temps/reglagesCommun';
import { SuiviDemande, DoitAgir, sansJargon } from '../../components/suivi/SuiviDemande';
import { useAuth } from '../../context/AuthContext';

/** Types du contrat (libellés identiques à ceux du serveur, prochainActeur.LIBELLES_TYPE). */
const TYPES = [
  ['CONGE', 'Congé'], ['PERMISSION', 'Permission'], ['RECUPERATION', 'Récupération'], ['MISSION', 'Mission'],
  ['DEMANDE_TEMPS', 'Oubli de pointage ou justification'], ['ARRET_MALADIE', 'Arrêt maladie'], ['REGULARISATION_TEMPS', 'Régularisation du temps'],
  ['PRET_SOGAS', 'Prêt SOGAS'], ['DOSSIER_SALARIAL', 'Dossier salarial (carrière)'],
  ['DS_PRET', 'Prêt social'], ['DS_AIDE', 'Aide sociale'], ['DS_PAIEMENT', 'Paiement (dispositif social)'], ['DS_CAMPAGNE', 'Campagne (dispositif social)'],
];
const ANCIENNETES = [['1', '1 jour et plus'], ['3', '3 jours et plus'], ['7', '7 jours et plus'], ['15', '15 jours et plus'], ['30', '30 jours et plus']];
const ROLES_RH = ['admin', 'drh', 'rh_principal', 'assistant_rh', 'directeur_general', 'directeur_adjoint'];
const FILTRES_VIDES = { type: '', anciennete_min: '', acteur_id: '', fonction: '', service_id: '', vacance: '' };

const LIBELLES_NON_COUVERTS = {
  IPM: 'IPM', DETTES: 'Prêts et retenues (dettes)', AJUSTEMENT_SOLDE_CONGES: 'Ajustements de solde de congés', HEURES_SUP: 'Heures supplémentaires',
  EVENEMENTS_SALARIES: 'Événements salariés', DS_DECISION: 'Décisions des dispositifs sociaux',
};
const tonAnciennete = (j) => (j === null || j === undefined ? 'neutre' : j >= 30 ? 'danger' : j >= 7 ? 'alerte' : 'neutre');

/** Écran du module où l'action réelle se fait (navigation seulement). */
function lienModule(l, role) {
  const org = l.suivi && l.suivi.gestion ? l.suivi.gestion.organisation_id : null;
  switch (l.type_dossier) {
    case 'CONGE': return '/conges';
    case 'PERMISSION': case 'RECUPERATION': case 'MISSION': case 'DEMANDE_TEMPS': case 'REGULARISATION_TEMPS':
      return ROLES_RH.includes(role) ? '/temps/rh/a-traiter' : '/temps/a-traiter';
    case 'ARRET_MALADIE': return '/temps/arrets';
    case 'PRET_SOGAS': return '/prets-sogas';
    case 'DOSSIER_SALARIAL': return l.demandeur && l.demandeur.id ? `/employes/${l.demandeur.id}?onglet=paie` : null;
    case 'DS_PRET': return org ? `/dispositifs/${org}/prets/${l.dossier_id}` : null;
    case 'DS_AIDE': return org ? `/dispositifs/${org}/aides/${l.dossier_id}` : null;
    case 'DS_PAIEMENT': return org ? `/dispositifs/${org}/tresorerie/paiements/${l.dossier_id}` : null;
    case 'DS_CAMPAGNE': return org ? `/dispositifs/${org}/campagnes/${l.dossier_id}` : null;
    default: return null;
  }
}

/** Choix des filtres « personne », « fonction », « service » : valeurs présentes dans la vue non filtrée. */
function optionsDe(lignes) {
  const personnes = new Map(); const fonctions = new Map(); const services = new Map();
  (lignes || []).forEach((l) => {
    const a = l.acteur || {};
    if (a.personne && a.personne.id && a.personne.nom_affiche) personnes.set(a.personne.id, a.personne.nom_affiche);
    const autres = (l.suivi && l.suivi.gestion && l.suivi.gestion.autres_acteurs) || [];
    autres.forEach((x) => { if (x && x.id && x.nom_affiche) personnes.set(x.id, x.nom_affiche); });
    if (a.fonction && a.fonction.code && a.fonction.libelle && a.fonction.code !== 'DEMANDEUR') fonctions.set(a.fonction.code, a.fonction.libelle);
    const dem = l.suivi && l.suivi.gestion && l.suivi.gestion.demandeur;
    if (dem && dem.service_id && dem.service) services.set(dem.service_id, dem.service);
  });
  const trier = (m) => [...m.entries()].map(([valeur, libelle]) => ({ valeur, libelle })).sort((x, y) => x.libelle.localeCompare(y.libelle, 'fr'));
  return { personnes: trier(personnes), fonctions: trier(fonctions), services: trier(services) };
}

function SelectFiltre({ libelle, valeur, onChange, options, tous }) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span className="rh-cellule-secondaire" style={{ display: 'block', marginBottom: 2 }}>{libelle}</span>
      <select className="rh-champ-saisie" value={valeur} onChange={(e) => onChange(e.target.value)}>
        <option value="">{tous}</option>
        {options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
      </select>
    </label>
  );
}

function FenetreDetail({ ligne, role, onFermer }) {
  const lien = lienModule(ligne, role);
  return (
    <Fenetre titre={`${ligne.type_libelle || 'Dossier'} — ${(ligne.demandeur && ligne.demandeur.nom_affiche) || 'sans demandeur'}`} Icone={Hourglass} onFermer={onFermer}>
      <div className="rh-cellule-secondaire" style={{ marginBottom: 4 }}>
        {[ligne.demandeur && ligne.demandeur.service, ligne.suivi && ligne.suivi.statut_libelle].filter(Boolean).join(' · ')}
      </div>
      <SuiviDemande suivi={ligne.suivi} circuit="ouvert" />
      {lien && (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <Link className="rh-btn rh-btn-primaire" to={lien}><ExternalLink size={15} aria-hidden="true" />Ouvrir dans le module</Link>
        </div>
      )}
    </Fenetre>
  );
}

export default function DossiersEnAttentePage() {
  const { employe } = useAuth();
  const [filtres, setFiltres] = useState(FILTRES_VIDES);
  const [base, setBase] = useState(null);       // vue non filtrée : options des filtres et compteurs
  const [donnees, setDonnees] = useState(null);  // vue filtrée (serveur)
  const [erreur, setErreur] = useState('');
  const [refus, setRefus] = useState('');
  const [plusRecents, setPlusRecents] = useState(false);
  const [page, setPage] = useState(1);
  const [taille, setTaille] = useState(25);
  const [detail, setDetail] = useState(null);

  const actifs = Object.values(filtres).some(Boolean);
  const charger = useCallback(async () => {
    setErreur('');
    const params = { limite: 500 };
    Object.entries(filtres).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const r = (await axios.get('/api/workflow/en-attente', { params })).data.data;
      setDonnees(r);
      if (!actifs) setBase(r);
    } catch (e) {
      setDonnees(null);
      const m = e?.response?.data?.message || 'Chargement impossible. Réessayez.';
      if (e?.response?.status === 403) setRefus(m); else setErreur(m);
    }
  }, [filtres, actifs]);
  useEffect(() => { charger(); }, [charger]);
  // Options des filtres : vue non filtrée, lue une fois même si l'écran s'ouvre déjà filtré.
  useEffect(() => {
    if (base || !actifs) return;
    axios.get('/api/workflow/en-attente', { params: { limite: 500 } }).then((r) => setBase(r.data.data)).catch(() => {});
  }, [base, actifs]);

  const options = useMemo(() => optionsDe(base && base.lignes), [base]);
  const lignes = useMemo(() => {
    const l = Array.isArray(donnees && donnees.lignes) ? donnees.lignes : [];
    return plusRecents ? [...l].reverse() : l; // le serveur trie déjà du plus ancien au plus récent
  }, [donnees, plusRecents]);
  const pages = Math.max(1, Math.ceil(lignes.length / taille));
  const visibles = lignes.slice((page - 1) * taille, page * taille);
  const nbVacances = ((base && base.lignes) || []).filter((l) => l.acteur && l.acteur.vacance).length;
  const maj = (k) => (v) => { setFiltres((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Pilotage' }, { libelle: 'Dossiers en attente' }]} titre={donnees ? `Dossiers en attente (${donnees.total})` : 'Dossiers en attente'}
          sousTitre="Chaque demande en cours, la personne qui doit agir et depuis quand. Lecture seule : l'action se fait dans le module." />

        {refus && <EtatVide Icone={Hourglass} titre="Accès non autorisé" texte={refus} />}
        {!refus && erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}

        {!refus && nbVacances > 0 && (
          <BandeauInfo ton="alerte" titre={`${nbVacances} dossier${nbVacances > 1 ? 's' : ''} bloqué${nbVacances > 1 ? 's' : ''} : aucun titulaire actif pour la fonction attendue`}
            action={filtres.vacance !== 'true' && <Bouton petit Icone={UserX} onClick={() => maj('vacance')('true')}>Les afficher</Bouton>}>
            Désignez un titulaire ou un intérimaire de la fonction pour débloquer ces dossiers.
          </BandeauInfo>
        )}

        {!refus && (
          <Carte>
            <div className="sv-filtres" role="group" aria-label="Filtres">
              <SelectFiltre libelle="Type" valeur={filtres.type} onChange={maj('type')} tous="Tous les types" options={TYPES.map(([valeur, libelle]) => ({ valeur, libelle }))} />
              <SelectFiltre libelle="Ancienneté" valeur={filtres.anciennete_min} onChange={maj('anciennete_min')} tous="Toutes" options={ANCIENNETES.map(([valeur, libelle]) => ({ valeur, libelle }))} />
              <SelectFiltre libelle="Personne qui doit agir" valeur={filtres.acteur_id} onChange={maj('acteur_id')} tous="Toutes les personnes" options={options.personnes} />
              <SelectFiltre libelle="Fonction" valeur={filtres.fonction} onChange={maj('fonction')} tous="Toutes les fonctions" options={options.fonctions} />
              <SelectFiltre libelle="Service du demandeur" valeur={filtres.service_id} onChange={maj('service_id')} tous="Tous les services" options={options.services} />
              <SelectFiltre libelle="Vacance" valeur={filtres.vacance} onChange={maj('vacance')} tous="Tous les dossiers" options={[{ valeur: 'true', libelle: 'Fonction vacante seulement' }]} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="rh-cellule-secondaire">{donnees ? `Périmètre : ${donnees.perimetre || '—'}` : ''}</span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {actifs && <Bouton petit variante="discret" onClick={() => { setFiltres(FILTRES_VIDES); setPage(1); }}>Effacer les filtres</Bouton>}
                <Bouton petit Icone={plusRecents ? ArrowUpNarrowWide : ArrowDownWideNarrow} onClick={() => { setPlusRecents((x) => !x); setPage(1); }}
                  aria-label={plusRecents ? 'Trier : plus anciens d\'abord' : 'Trier : plus récents d\'abord'}>
                  {plusRecents ? 'Plus récents d\'abord' : 'Plus anciens d\'abord'}
                </Bouton>
              </span>
            </div>

            {!donnees && !erreur ? <><Squelette largeur="70%" /><Squelette largeur="50%" style={{ marginTop: 8 }} /></> : donnees && lignes.length === 0 ? (
              <EtatVide Icone={CheckCircle2} titre="Aucun dossier en attente" texte={actifs ? 'Aucun dossier ne correspond à ces filtres.' : 'Rien n\'attend d\'action dans votre périmètre.'} />
            ) : donnees && (
              <div className="rh-conteneur-tableau">
                <table className="rh-tableau">
                  <thead>
                    <tr><th>Type</th><th>Demandeur</th><th>Étape</th><th>Doit agir</th><th>Depuis</th><th>Ancienneté</th><th aria-label="Détail" /></tr>
                  </thead>
                  <tbody>
                    {visibles.map((l) => {
                      const vacance = !!(l.acteur && l.acteur.vacance);
                      return (
                        <tr key={`${l.type_dossier}-${l.dossier_id}`} className={vacance ? 'sv-ligne-vacante' : undefined}>
                          <td><span className="rh-cellule-principale">{l.type_libelle || l.type_dossier}</span></td>
                          <td>
                            <span className="rh-cellule-principale" style={{ display: 'block' }}>{(l.demandeur && l.demandeur.nom_affiche) || '—'}</span>
                            {l.demandeur && l.demandeur.service && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.demandeur.service}</span>}
                          </td>
                          <td>{(l.etape && l.etape.libelle) || '—'}</td>
                          <td><DoitAgir suivi={l.suivi} />{vacance && <Badge ton="alerte">Fonction vacante</Badge>}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formaterDate(l.depuis) || '—'}</td>
                          <td><Badge ton={tonAnciennete(l.attente_jours)} point={l.attente_jours >= 7}>{l.attente_libelle || 'Inconnue'}</Badge></td>
                          <td><Bouton petit variante="discret" Icone={Eye} onClick={() => setDetail(l)} aria-label={`Détail : ${l.type_libelle || ''} de ${(l.demandeur && l.demandeur.nom_affiche) || ''}`}>Détail</Bouton></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {donnees && lignes.length > 0 && (
              <Pagination page={Math.min(page, pages)} pages={pages} total={lignes.length} taille={taille}
                onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} onTaille={(t) => { setTaille(t); setPage(1); }} tailles={[10, 25, 50, 100]} />
            )}
            {donnees && donnees.total > lignes.length && (
              <p className="rh-cellule-secondaire" style={{ marginTop: 8 }}>Seuls les {lignes.length} dossiers les plus anciens sont affichés sur {donnees.total} : affinez les filtres.</p>
            )}
          </Carte>
        )}

        {!refus && donnees && Array.isArray(donnees.non_couverts) && donnees.non_couverts.length > 0 && (
          <details className="rh-carte" style={{ padding: '12px 16px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>Demandes non suivies dans cette vue ({donnees.non_couverts.length})</summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--sg-texte-2)' }}>
              {donnees.non_couverts.map((n) => {
                const raison = sansJargon(n.raison);
                return <li key={n.type}><strong style={{ fontWeight: 500 }}>{LIBELLES_NON_COUVERTS[n.type] || String(n.type || '').toLowerCase()}</strong>{raison ? ` — ${raison}` : ''}</li>;
              })}
            </ul>
          </details>
        )}

        {detail && <FenetreDetail ligne={detail} role={employe && employe.role} onFermer={() => setDetail(null)} />}
      </div>
    </Layout>
  );
}
